import { Menu as MenuIcon } from '@mui/icons-material';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useRouterState } from '@tanstack/react-router';
import { type ReactNode, useCallback, useState } from 'react';

import { NotificationBellButton } from '@/components/Notifications';
import { SiteFooter } from '@/components/SiteFooter/SiteFooter';
import { HEADER_HEIGHT, SiteHeader } from '@/components/SiteHeader/SiteHeader';
import { useAuthContext } from '@/context/AuthContext';
import { NotificationStreamProvider } from '@/context/NotificationStreamContext';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import {
  ADMIN_ITEMS,
  DASHBOARD_ITEMS,
  GALLERY_ITEMS,
  GALLERY_OVERVIEW_PATH,
  type NavItem,
} from './navItems';

export const SIDEBAR_KEY = 'ig_sidebar_open';
const DRAWER_WIDTH = 300;

function readStored(): boolean | null {
  try {
    const v = window.localStorage.getItem(SIDEBAR_KEY);

    return v === null ? null : v === 'true';
  } catch {
    return null;
  }
}

function writeStored(open: boolean) {
  try {
    window.localStorage.setItem(SIDEBAR_KEY, String(open));
  } catch {
    // Storage can be blocked: the choice then lasts for this visit only.
  }
}

// One shell for the whole site: the header on top, the sidebar for a signed-in
// account, then the page and the footer.
export function AppShell({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const { isAuthenticated, account, logout } = useAuthContext();
  const { canCall, hasPermission, isSystemAdmin, isLoading } = usePermissionCheck();
  const currentPath = useRouterState().location.pathname;
  // Closed until the person opens it; the remembered choice applies on desktop.
  const [open, setOpen] = useState<boolean>(() => (isMobile ? false : (readStored() ?? false)));

  const toggle = useCallback(() => {
    setOpen((prev) => {
      writeStored(!prev);

      return !prev;
    });
  }, []);

  const isGallery =
    currentPath.startsWith('/gallery') ||
    (!isSystemAdmin() && hasPermission('gallery-artworks.create'));

  const isVisible = (item: NavItem): boolean => {
    if (item.canCall && !canCall(item.canCall[0], item.canCall[1])) return false;
    if (item.withoutPermission) {
      // Wait for the permissions, so "Apply" does not flash for a gallery.
      return !isLoading && !hasPermission(item.withoutPermission);
    }
    if (item.anyPermission) return item.anyPermission.some((key) => hasPermission(key));

    return !item.permission || hasPermission(item.permission);
  };

  const isActive = (path: string) => {
    if (path === '/dashboard' || path === '/admin' || path === GALLERY_OVERVIEW_PATH) {
      return currentPath === path || currentPath === `${path}/`;
    }

    return currentPath.startsWith(path);
  };

  const mainItems = (isGallery ? GALLERY_ITEMS : DASHBOARD_ITEMS).filter(isVisible);
  const adminItems = isGallery ? [] : ADMIN_ITEMS.filter(isVisible);

  const renderItem = (item: NavItem) => {
    const active = isActive(item.path);

    return (
      <ListItemButton
        key={item.path}
        component={Link}
        to={item.path}
        selected={active}
        onClick={isMobile ? toggle : undefined}
        aria-current={active ? 'page' : undefined}
        sx={(t) => ({
          py: '9px',
          px: 4,
          borderRadius: 0,
          borderLeft: '2px solid',
          borderLeftColor: 'transparent',
          color: t.palette.ig.textSecondary,
          fontSize: 14,
          '&:hover': { backgroundColor: t.palette.ig.raisedStrong },
          '&.Mui-selected': {
            color: t.palette.text.primary,
            backgroundColor: t.palette.ig.raisedStrong,
            borderLeftColor: t.palette.primary.main,
            '&:hover': { backgroundColor: t.palette.ig.raisedStrong },
          },
        })}
      >
        <ListItemText
          primary={item.label}
          slotProps={{ primary: { sx: { fontSize: 14, color: 'inherit' } } }}
          sx={{ m: 0 }}
        />
      </ListItemButton>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        component="nav"
        sx={{ flex: 1, py: 4, overflowY: 'auto' }}
        aria-label={isGallery ? 'gallery navigation' : 'dashboard navigation'}
      >
        <Typography variant="label" component="p" sx={{ m: 0, px: 4, pb: 1.5 }}>
          My space
        </Typography>
        <List disablePadding>{mainItems.map(renderItem)}</List>
        {adminItems.length > 0 && (
          <>
            <Typography variant="label" component="p" sx={{ m: 0, px: 4, pt: 3.5, pb: 1.5 }}>
              Administration
            </Typography>
            <List disablePadding>{adminItems.map(renderItem)}</List>
          </>
        )}
      </Box>
      <Box sx={{ px: 4, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" noWrap sx={{ color: 'text.secondary', mb: 1 }}>
          {account?.email}
        </Typography>
        <ListItemButton
          onClick={logout}
          sx={{ borderRadius: '2px', px: 1, color: 'text.secondary' }}
          aria-label="sign out"
        >
          <ListItemText
            primary="Sign Out"
            slotProps={{ primary: { sx: { fontSize: 14, color: 'inherit' } } }}
            sx={{ m: 0 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  const paperSx = {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    top: HEADER_HEIGHT,
    height: { xs: `calc(100% - ${HEADER_HEIGHT.xs}px)`, md: `calc(100% - ${HEADER_HEIGHT.md}px)` },
    backgroundColor: 'background.paper',
    borderRight: '1px solid',
    borderColor: 'divider',
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteHeader
        leading={
          isAuthenticated ? (
            <IconButton
              color="inherit"
              onClick={toggle}
              aria-label={open ? 'hide navigation' : 'show navigation'}
              aria-expanded={open}
              sx={{
                width: 44,
                height: 44,
                borderRadius: '2px',
                backgroundColor: 'ig.raisedStrong',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'ig.raisedStrong' },
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : undefined
        }
        actions={
          isAuthenticated ? (
            <NotificationStreamProvider>
              <NotificationBellButton />
            </NotificationStreamProvider>
          ) : undefined
        }
      />
      <Box sx={{ display: 'flex', flex: 1 }}>
        {isAuthenticated && (
          <Drawer
            variant={isMobile ? 'temporary' : 'persistent'}
            open={open}
            onClose={toggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              width: isMobile ? 0 : open ? DRAWER_WIDTH : 0,
              flexShrink: 0,
              transition: theme.transitions.create('width'),
              '& .MuiDrawer-paper': paperSx,
            }}
          >
            {drawerContent}
          </Drawer>
        )}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1 }}>{children}</Box>
          <SiteFooter />
        </Box>
      </Box>
    </Box>
  );
}

export default AppShell;
